import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { UiAutocompleteFieldComponent } from '../ui-autocomplete-field/ui-autocomplete-field.component';
import { UiAutocompleteOption } from '../ui-autocomplete-field/ui-autocomplete-field.types';
import { UiFormFieldComponent } from '../ui-form-field/ui-form-field.component';
import { UiStockProductOption, UiStockSelectorValue, UiStockVariantOption } from './ui-stock-selector.types';

@Component({
  selector: 'ui-stock-selector',
  standalone: true,
  imports: [UiAutocompleteFieldComponent, UiFormFieldComponent],
  templateUrl: './ui-stock-selector.component.html',
  styleUrl: './ui-stock-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiStockSelectorComponent {
  readonly value = input<UiStockSelectorValue>({ categoryId: '', productId: '', variantId: '', quantity: null });
  readonly categories = input<UiAutocompleteOption[]>([]);
  readonly products = input<UiStockProductOption[]>([]);
  readonly variants = input<UiStockVariantOption[]>([]);
  readonly categoryLabel = input('Category');
  readonly productLabel = input('Product');
  readonly variantLabel = input('Variant');
  readonly quantityLabel = input('Quantity');
  readonly quantityError = input('');
  readonly valueChange = output<UiStockSelectorValue>();

  readonly selectedCategoryText = computed(() => this.labelFor(this.categories(), this.value().categoryId));
  readonly selectedProductText = computed(() => this.labelFor(this.filteredProducts(), this.value().productId));
  readonly selectedVariantText = computed(() => this.labelFor(this.filteredVariants(), this.value().variantId));

  readonly filteredProducts = computed(() => {
    const categoryId = this.value().categoryId;
    return this.products().filter((product) => !categoryId || product.categoryId === categoryId);
  });

  readonly filteredVariants = computed(() => {
    const productId = this.value().productId;
    return this.variants().filter((variant) => !productId || variant.productId === productId);
  });

  onCategoryQuery(value: string): void {
    if (!value.trim()) {
      this.emit({ categoryId: '', productId: '', variantId: '' });
    }
  }

  onProductQuery(value: string): void {
    if (!value.trim()) {
      this.emit({ productId: '', variantId: '' });
    }
  }

  onVariantQuery(value: string): void {
    if (!value.trim()) {
      this.emit({ variantId: '' });
    }
  }

  selectCategory(categoryId: string): void {
    // Parent selections reset children so invalid category/product/variant combinations cannot be submitted.
    this.emit({ categoryId, productId: '', variantId: '' });
  }

  selectProduct(productId: string): void {
    this.emit({ productId, variantId: '' });
  }

  selectVariant(variantId: string): void {
    this.emit({ variantId });
  }

  setQuantity(value: string): void {
    const parsed = Number(value);
    this.emit({ quantity: Number.isFinite(parsed) ? parsed : null });
  }

  private emit(patch: Partial<UiStockSelectorValue>): void {
    this.valueChange.emit({ ...this.value(), ...patch });
  }

  private labelFor(options: UiAutocompleteOption[], value: string): string {
    return options.find((option) => option.value === value)?.label ?? value;
  }
}
